/**
 * "Loading" vs "No games" (Dan, 2026-10-02: "when I toggle, the main page
 * says 'no [sport] games' while it's loading — can it say loading first, and
 * then display no games once it's determined that is actually true?").
 *
 * With consumer feed filters a tab holds only the rows of what it views, so
 * a sport toggle starts EMPTY: the board picks its rows from the owning
 * edge's index, the transports hold the previous scope through the flicker
 * window, the engine acks the new filter and then streams a budget-paced
 * replacement snapshot. Until that has happened an empty board means "not
 * here yet", not "none exist". This decides which, from what the tab can
 * actually observe:
 *
 *  - the board's own index (`useBoardFeedNeeds` status): while it is in
 *    flight the board is loading; once it says the window has NO games the
 *    board is empty for real, at once; once it named rows, the board is
 *    loading until the owning engine has acked that pick and had
 *    `FEED_SETTLE_MS` to deliver the snapshot;
 *  - otherwise the engine that serves the sport (live ownership): loading
 *    until its installed filter admits the sport and `FEED_SETTLE_MS` has
 *    passed since that ack (or since the change, if the scope already
 *    covered it); a firehose (no filter) settles after `FEED_SETTLE_MS`;
 *    a sport no engine serves settles after `OWNERLESS_GRACE_MS` (an
 *    announce may still be on its way);
 *  - never longer than `FEED_SETTLE_MAX_MS` after the change: a feed that
 *    never answers must not leave the board saying "Loading" forever. It
 *    does not say "No games" either (D34): see `feedSettlingState`.
 *
 * No fleet registered (unwired tests, the feed flag off with no clients) =
 * nothing to wait on = never settling. Pure; the hook polls it.
 */
import type { FeedFilterState, FeedOwnership } from '@/stores/marketStore';
import {
  NO_ROWS_TICKER, familyServedBy, gameKeySportFamily, tickerSportFamily,
  type MarketFilterEffective,
} from '@/workers/feedFilter';
import type { BoardFeedStatus } from '@/hooks/useBoardFeedNeeds';

/** After the ack that admits the sport: time for the replacement snapshot
 *  (budget-paced — he2's 15k-row CFB slate drains in about a second). */
export const FEED_SETTLE_MS = 1_500;
/** A sport no engine announces: how long an announce may still be coming. */
export const OWNERLESS_GRACE_MS = 3_000;
/** Hard cap: after this the board shows what it has, honestly. */
export const FEED_SETTLE_MAX_MS = 10_000;

/** Does an installed (acked) scope admit rows of `sportWire` (uppercase)? */
export function scopeAdmitsSport(effective: MarketFilterEffective | null, sportWire: string): boolean {
  if (effective == null) return true; // legacy / unfiltered connection
  const one = new Set([sportWire]);
  if (effective.tickers && effective.tickers.length > 0) {
    if (effective.tickers.every((t) => t === NO_ROWS_TICKER)) return false;
    return effective.tickers.some((t) => t !== NO_ROWS_TICKER && familyServedBy(tickerSportFamily(t), one));
  }
  if (effective.sports && effective.sports.length > 0
    && !effective.sports.some((s) => s.trim().toUpperCase() === sportWire)) return false;
  if (effective.games && effective.games.length > 0
    && !effective.games.some((g) => familyServedBy(gameKeySportFamily(g), one))) return false;
  return true;
}

export interface FeedSettlingInput {
  now: number;
  /** When the board's selection (sport / reading / window / filters) last changed. */
  changedAt: number;
  /** The board's sport (feed key, lowercase); null = every sport. */
  sport: string | null;
  feeds: readonly FeedOwnership[];
  states: readonly FeedFilterState[];
  /** The board's own declaration status, when the board is line-scoped. */
  board: BoardFeedStatus | null;
}

/**
 * D34 — what an empty board may say. `settling`: still waiting ("Loading").
 * `settled`: the feed answered and the board is truly empty ("No events").
 * `unanswered`: the cap passed, or no engine announced the sport after the
 * grace period, without an answer ("the feed isn't answering"). `rejected`:
 * the owning engine refused the board's filter. A late answer still moves
 * an unanswered board to `settled`, so the caller keeps checking.
 */
export type FeedSettlingState =
  | { kind: 'settling' }
  | { kind: 'settled' }
  | { kind: 'unanswered' }
  | { kind: 'rejected'; error: string };

const SETTLING: FeedSettlingState = { kind: 'settling' };
const SETTLED: FeedSettlingState = { kind: 'settled' };
const UNANSWERED: FeedSettlingState = { kind: 'unanswered' };

export function feedSettlingState(input: FeedSettlingInput): FeedSettlingState {
  const { now, changedAt, feeds } = input;
  if (feeds.length === 0) return SETTLED;
  const verdict = uncappedVerdict(input);
  // Past the cap the board stops saying "Loading", but a feed that still has
  // not answered is not "No events" either.
  if (verdict.kind === 'settling' && now - changedAt >= FEED_SETTLE_MAX_MS) return UNANSWERED;
  return verdict;
}

/** The settling decision without the hard cap. */
function uncappedVerdict(input: FeedSettlingInput): FeedSettlingState {
  const { now, changedAt, sport, feeds, states, board } = input;
  const elapsed = now - changedAt;
  const stateOf = (feedId: string | null | undefined): FeedFilterState | undefined =>
    feedId == null ? undefined : states.find((s) => s.feedId === feedId);
  const within = (ms: number): FeedSettlingState => (ms > 0 ? SETTLING : SETTLED);

  // The board's own index, when it scopes the board.
  if (board != null) {
    if (board.pending) return SETTLING;
    if (board.mode === 'tickers' && board.indexedAtMs != null) {
      if (board.games === 0) return SETTLED;
      const st = stateOf(board.owner);
      if (st?.error) return { kind: 'rejected', error: st.error };
      if (st == null || st.ackMs < board.indexedAtMs) return SETTLING;
      return within(FEED_SETTLE_MS - (now - st.ackMs));
    }
  }

  if (sport == null) return within(FEED_SETTLE_MS - elapsed);
  const owner = feeds.find((f) => f.sports.includes(sport as never))
    ?? feeds.find((f) => f.floorSports.includes(sport as never));
  // No engine announced the sport: past the grace period nothing is serving it.
  if (owner == null) return elapsed < OWNERLESS_GRACE_MS ? SETTLING : UNANSWERED;
  if (owner.role === 'firehose') return within(FEED_SETTLE_MS - elapsed);
  const st = stateOf(owner.id);
  if (st?.error) return { kind: 'rejected', error: st.error };
  if (st == null || st.ackMs <= 0) return SETTLING;
  if (!scopeAdmitsSport(st.effective, sport.toUpperCase())) return SETTLING;
  return within(FEED_SETTLE_MS - (now - Math.max(st.ackMs, changedAt)));
}

/** Is the board still loading? (`feedSettlingState` says what to show instead.) */
export function feedSettling(input: FeedSettlingInput): boolean {
  return feedSettlingState(input).kind === 'settling';
}
